import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, RefreshCw, Loader2, Target, ArrowRight, Check, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

/**
 * Park-Lot Sweep modal — the AI Growth Mentor scans the user's pending
 * parked items, picks 2-3 highest-leverage ones, proposes a mission per
 * pick, and lets the user one-tap convert any pick to their active mission.
 */
export default function ParkLotSweepModal({ open, onClose }) {
  const { t } = useTranslation();
  const [sweep, setSweep] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [convertingId, setConvertingId] = useState(null);

  const load = async (refresh = false) => {
    if (refresh) setRegenerating(true); else setLoading(true);
    try {
      const url = refresh
        ? '/users/me/park-lot-sweep/regenerate'
        : '/users/me/park-lot-sweep';
      const { data } = refresh ? await api.post(url) : await api.get(url);
      setSweep(data);
    } catch (e) {
      toast.error(t('c.dashboard.ParkLotSweepModal.could_not_load_sweep'));
    } finally {
      setLoading(false);
      setRegenerating(false);
    }
  };

  useEffect(() => {
    if (open && !sweep) load(false);
    // intentionally only on open — we want the cached sweep on next open
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConvert = async (pickId) => {
    if (convertingId) return;
    setConvertingId(pickId);
    try {
      await api.post(`/users/me/park-lot-sweep/convert/${pickId}`);
      toast.success(t('c.dashboard.ParkLotSweepModal.mission_live_open_the_goal_to'));
      // Reload so the converted indicator shows.
      await load(false);
      // Trigger refresh in dashboard widgets.
      try { window.dispatchEvent(new CustomEvent('roadmap:refresh')); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('c.dashboard.ParkLotSweepModal.could_not_convert'));
    } finally {
      setConvertingId(null);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
        onClick={onClose}
        data-testid="park-lot-sweep-modal"
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          className="bg-white dark:bg-[#1E293B] rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header — purple glow matching the AI Marketplace flagship */}
          <div
            className="relative px-4 py-4 rounded-t-3xl text-white overflow-hidden"
            style={{ background: 'radial-gradient(circle at 0% 0%, #5B21B6 0%, #0F172A 60%, #000 100%)' }}
          >
            <span className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full bg-[#7E5BEF] opacity-30 blur-3xl" aria-hidden />
            <div className="relative flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-white/15 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg flex-shrink-0">
                <Sparkles size={20} strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase font-extrabold tracking-widest opacity-90">{t('c.dashboard.ParkLotSweepModal.weekly_ai')}</p>
                <h3 className="text-lg font-extrabold leading-tight">{t('c.dashboard.ParkLotSweepModal.park_lot_sweep')}</h3>
                <p className="text-[11px] font-bold opacity-90 mt-0.5 leading-snug"> {t('c.dashboard.ParkLotSweepModal.your_mentor_picks_2_3_highest')} </p>
              </div>
              <button onClick={onClose} className="p-1 -mt-1 -mr-1 opacity-80" data-testid="park-lot-sweep-close">
                <X size={20} strokeWidth={3} />
              </button>
            </div>
          </div>

          <div className="p-4 space-y-3">
            {(loading || regenerating) && (
              <div className="flex items-center justify-center py-8 text-sm text-[#7E5BEF] font-bold gap-2">
                <Loader2 size={16} className="animate-spin" />
                {regenerating ? t('c.dashboard.ParkLotSweepModal.re_analysing_your_park_lot') : t('c.dashboard.ParkLotSweepModal.loading_your_sweep')}
              </div>
            )}

            {!loading && sweep && (
              <>
                {sweep.rationale_summary && (
                  <div className="rounded-xl bg-[#EDE9FE] dark:bg-[#1E1B4B] p-3 border border-[#C4B5FD] dark:border-[#312E81]">
                    <p className="text-[10px] uppercase font-extrabold text-[#7E5BEF] tracking-wider mb-1"> {t('c.dashboard.ParkLotSweepModal.mentor_s_read')} </p>
                    <p className="text-xs font-bold text-[#0F172A] dark:text-[#E0E7FF] leading-relaxed" data-testid="park-lot-sweep-summary">
                      {sweep.rationale_summary}
                    </p>
                  </div>
                )}

                {(!sweep.picks || sweep.picks.length === 0) && (
                  <div className="text-center py-8" data-testid="park-lot-sweep-empty">
                    <p className="text-sm font-extrabold text-[#94A3B8]"> {t('c.dashboard.ParkLotSweepModal.add_a_few_items_to_your')} </p>
                  </div>
                )}

                <div className="space-y-2.5" data-testid="park-lot-sweep-picks">
                  {(sweep.picks || []).map((p) => (
                    <PickCard
                      key={p.pick_id}
                      pick={p}
                      converting={convertingId === p.pick_id}
                      onConvert={() => onConvert(p.pick_id)}
                    />
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2">
                  <p className="text-[10px] font-extrabold text-[#94A3B8]">
                    {sweep.items_considered || 0} {t('c.dashboard.ParkLotSweepModal.items_considered')} {sweep.from_cache ? t('c.dashboard.ParkLotSweepModal.cached') : t('c.dashboard.ParkLotSweepModal.fresh')}
                  </p>
                  <button
                    onClick={() => load(true)}
                    disabled={regenerating}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#F1F5F9] dark:bg-[#0F172A] text-[#7E5BEF] text-[11px] font-extrabold disabled:opacity-50"
                    data-testid="park-lot-sweep-regen"
                  >
                    {regenerating ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} strokeWidth={3} />} {t('c.dashboard.ParkLotSweepModal.regenerate')} </button>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function PickCard({ pick, converting, onConvert }) {
  const { t } = useTranslation();
  const converted = !!pick.converted_mission_id;
  return (
    <div
      className="rounded-2xl border-2 border-[#E5E7EB] dark:border-[#334155] bg-white dark:bg-[#0F172A] p-3 space-y-2"
      data-testid={`park-lot-sweep-pick-${pick.pick_id}`}
    >
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 rounded-xl bg-[#EDE9FE] dark:bg-[#1E1B4B] flex items-center justify-center flex-shrink-0">
          <Target size={14} strokeWidth={3} color="#7E5BEF" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase font-extrabold text-[#94A3B8] tracking-wider truncate">
            {pick.goal_title || '—'}
          </p>
          <p className="text-sm font-extrabold leading-tight">{pick.item_title}</p>
        </div>
      </div>

      <div className="rounded-xl bg-[#F8FAFC] dark:bg-[#1E293B] p-2.5">
        <p className="text-[10px] font-extrabold uppercase text-[#16C47F] tracking-wider mb-0.5"> {t('c.dashboard.ParkLotSweepModal.proposed_mission')} </p>
        <p className="text-sm font-extrabold leading-tight">{pick.mission?.title}</p>
        {pick.mission?.description && (
          <p className="text-[11px] font-bold text-[#475569] dark:text-[#94A3B8] mt-1 leading-relaxed">
            {pick.mission.description}
          </p>
        )}
        <div className="flex items-center gap-3 mt-1.5 text-[10px] font-extrabold text-[#94A3B8]">
          <span className="inline-flex items-center gap-0.5">
            <Clock size={9} strokeWidth={3} /> ~{pick.mission?.estimated_minutes}m
          </span>
          <span className="text-[#FBBF24]">{t('c.dashboard.ParkLotSweepModal.reward_xp_xp', { reward_xp: pick.mission?.reward_xp })}</span>
        </div>
      </div>

      {pick.rationale && (
        <p className="text-[11px] font-bold italic text-[#475569] dark:text-[#94A3B8] leading-relaxed px-1">
          &ldquo;{pick.rationale}&rdquo;
        </p>
      )}

      <button
        onClick={onConvert}
        disabled={converting || converted}
        className={`w-full py-2 rounded-xl text-xs font-extrabold inline-flex items-center justify-center gap-1 transition-colors ${
          converted
            ? 'bg-[#DCFCE7] text-[#16A34A]'
            : 'bg-[#7E5BEF] text-white hover:bg-[#6D44D8]'
        } disabled:opacity-50`}
        data-testid={`park-lot-sweep-convert-${pick.pick_id}`}
      >
        {converting && <Loader2 size={13} className="animate-spin" />}
        {!converting && converted && <><Check size={13} strokeWidth={3} /> {t('c.dashboard.ParkLotSweepModal.mission_live')}</>}
        {!converting && !converted && <>{t('c.dashboard.ParkLotSweepModal.make_it_my_next_mission')} <ArrowRight size={13} strokeWidth={3} /></>}
      </button>
    </div>
  );
}
